import type { Directive } from '@vue/runtime-core'
import { DirectiveBinding } from 'vue'
/**
 * Exec function when element resizes
 * v-element-resize
 */
const elementResize: Directive<HTMLElementResize, HTMLElementResizeListener> = {
  mounted(el, binding) {
    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        binding.value(entry.target, entry.contentRect)
      }
    })
    observer.observe(el)
    el.__resizeObserver__ = observer
  },
  updated(el, binding) {
    binding.value(el, el.getBoundingClientRect())
  },
  unmounted(el) {
    if (el.__resizeObserver__) {
      el.__resizeObserver__.disconnect()
      delete el.__resizeObserver__
    }
  },
}
export type HTMLElementResizeListener = (el: Element, rect: DOMRectReadOnly) => any
type HTMLElementResize = {
  __resizeObserver__?: ResizeObserver
} & HTMLElement

/**
 * Spinning effect on element click
 * v-resize
 */
const spin: Directive<HTMLElementSpinListener> = {
  mounted(el) {
    el.__SPIN_LISTENER__ = (_ev: MouseEvent) => {
      el.animate(
        [
          { transform: 'rotate(0deg)' },
          { transform: 'rotate(-180deg)' },
          { transform: 'rotate(360deg)' },
          { transform: 'rotate(720deg)' },
        ],
        {
          duration: 1500,
          easing: 'ease-in-out',
        },
      )
    }
    el.addEventListener('click', el.__SPIN_LISTENER__)
  },
  unmounted(el) {
    if (el.__SPIN_LISTENER__) {
      el.removeEventListener('click', el.__SPIN_LISTENER__)
      delete el.__SPIN_LISTENER__
    }
  },
}
type HTMLElementSpinListener = HTMLElement & {
  __SPIN_LISTENER__?: (ev: MouseEvent) => void
}

/**
 * Rotate Element using Web Animations API
 * https://developer.mozilla.org/en-US/docs/Web/API/Web_Animations_API
 *
 * @Example: v-rotate:[90].instant="boolean"
 */
const rotate: Directive<HTMLElement, boolean, 'instant'> = {
  beforeMount: (el, binding) => {
    if (binding.modifiers.instant) {
      el.style.transition = 'none'
      el.style.rotate = `${binding.arg}deg`
    }
  },
  updated: (el, binding) => {
    if (binding.modifiers.instant) {
      if (binding.value) {
        el.style.rotate = `${binding.arg}deg`
      } else {
        el.style.rotate = `0deg`
      }
      return
    }

    if (binding.value) {
      el.animate([{ transform: `rotate(${binding.arg}deg)` }], {
        duration: 300,
        fill: 'forwards',
        easing: 'ease-in-out',
      })
    } else {
      el.animate([{ transform: `rotate(0deg)` }], {
        duration: 300,
        fill: 'forwards',
        easing: 'ease-in-out',
      })
    }
  },
}

/**
 * Render HTML Node
 */
const appendElement: Directive<HTMLElement, HTMLElement | null> = {
  created(el, binding) {
    if (binding.value) {
      el.innerHTML = ''
      el.appendChild(binding.value)
    } else {
      warnEmptyBinding(el, binding)
    }
  },
  updated(el, binding) {
    // updated fires on every host re-render: skip the detach/re-append
    // (and its forced layout, expensive for large SVGs) if the element is unchanged
    if (binding.value === binding.oldValue && binding.value?.parentNode === el) {
      return
    }
    if (binding.value) {
      el.innerHTML = ''
      el.appendChild(binding.value)
    } else {
      warnEmptyBinding(el, binding)
    }
  },
  unmounted(el, binding) {
    if (binding.value && el.contains(binding.value)) {
      el.removeChild(binding.value)
    } else {
      warnEmptyBinding(el, binding)
    }
  },
}
function warnEmptyBinding(el: HTMLElement, binding: DirectiveBinding) {
  if (binding.value === null) {
    console.warn(`v-append-element: ${el.tagName} is missing a binding value`)
  }
}

const tableColumnResize: Directive = {
  updated(el) {
    const table: HTMLTableElement | null = el instanceof HTMLTableElement ? el : el.querySelector('table')
    const thead: HTMLTableSectionElement | undefined | null = table?.querySelector('thead')
    const ths: NodeListOf<HTMLTableCellElement> | undefined | null = thead?.querySelectorAll('th')

    if (!table || !thead || !ths || ths.length < 2) {
      return
    }

    const barHeight = thead.offsetHeight

    const resizeContainer = document.createElement('div')
    table.style.position = 'relative'
    resizeContainer.style.position = 'relative'
    resizeContainer.style.width = table.offsetWidth + 'px'
    resizeContainer.className = 'vue-columns-resizable'
    table.parentElement?.insertBefore(resizeContainer, table)

    let moving = false
    let movingIndex = 0

    const thsList = Array.from(ths)
    for (const th of ths) {
      const index = thsList.indexOf(th)
      th.style.width = th.offsetWidth + 'px'

      if (index + 1 >= ths.length) continue

      const nextTh = ths[index + 1]
      const bar = document.createElement('div')

      bar.style.position = 'absolute'
      bar.style.left = nextTh.offsetLeft - 4 + 'px'
      bar.style.top = '0'
      bar.style.height = barHeight + 'px'
      bar.style.width = '8px'
      bar.style.cursor = 'col-resize'
      bar.style.zIndex = '3'
      bar.style.background = '#ccc'
      bar.className = 'columns-resize-bar'

      bar.addEventListener('mousedown', () => {
        moving = true
        movingIndex = index
        document.body.style.cursor = 'col-resize'
        document.body.style.userSelect = 'none'
      })

      resizeContainer.appendChild(bar)
    }

    const bars = resizeContainer.querySelectorAll<HTMLElement>('.columns-resize-bar')
    document.addEventListener('mouseup', () => {
      if (!moving) return

      moving = false
      document.body.style.cursor = ''
      document.body.style.userSelect = ''

      bars.forEach((bar, index) => {
        const th = ths[index]
        const nextTh = ths[index + 1]
        th.style.width = th.offsetWidth + 'px'
        bar.style.left = nextTh.offsetLeft - 4 + 'px'
      })
    })

    const cutPx = (str: string) => +str.replace('px', '')

    const handleResize = (e: MouseEvent) => {
      if (moving) {
        const th = ths[movingIndex]
        const nextTh = ths[movingIndex + 1]
        const bar = bars[movingIndex]
        // th.style.width = cutPx(th.style.width) + e.movementX + 'px'
        // nextTh.style.width = cutPx(nextTh.style.width) - e.movementX + 'px'

        bar.style.left = cutPx(bar.style.left) + e.movementX + 'px'
        console.log(e.movementX, bar.style.left)
      }
    }

    resizeContainer.addEventListener('mousemove', handleResize)
    table.addEventListener('mousemove', handleResize)
  },
}

/**
 * @example
 * defineComponent({
 *  ...
 *  directives: AppDirectives
 * })
 */
const AppDirectives = {
  spin,
  rotate,
  elementResize,
  appendElement,
  tableColumnResize,
} satisfies Record<string, Directive>
export default AppDirectives

type AppDirectivesTypes = { [key in keyof typeof AppDirectives]: Directive }

declare module 'vue' {
  interface GlobalComponents extends AppDirectivesTypes {}
  interface GlobalDirectives extends AppDirectivesTypes {}
  export interface ComponentCustomProperties extends AppDirectivesTypes {}
}
// TODO - wait/check for intellij to respect "declare module 'vue'"
declare module '@vue/runtime-core' {
  interface GlobalComponents extends AppDirectivesTypes {}
  interface GlobalDirectives extends AppDirectivesTypes {}
  export interface ComponentCustomProperties extends AppDirectivesTypes {}
}
